import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { documentsService, GlobalDocument } from '@/api/documents'
import { Main } from '@/components/layout/main'
import { PageHeader } from '@/components/layout/page-header'
import { DataTable } from '@/components/table'
import DataTableSkeleton from '@/components/ui/data-table-skeleton'
import { getColumns } from './components/documents-columns'
import { DocumentsToolbar } from './components/documents-toolbar'
import { FolderArchive } from 'lucide-react'

export interface DocumentsTableParams {
  page: number
  page_size: number
  ordering: string | null
  search: string | null
  content_type: string | null
  /** When true, use full-text OCR search endpoint instead of listing */
  fullText: boolean
}

export default function Documents() {
  const { t } = useTranslation()
  const [documents, setDocuments] = useState<GlobalDocument[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [pagesCnt, setPagesCnt] = useState(0)

  const [params, setParams] = useState<DocumentsTableParams>({
    page: 0,
    page_size: 20,
    ordering: '-created_at',
    search: null,
    content_type: null,
    fullText: false,
  })

  useEffect(() => {
    setLoading(true)

    const promise =
      params.fullText && params.search
        ? // Full-text OCR search
          documentsService.search({
            q: params.search,
            limit: params.page_size,
            offset: params.page,
          })
        : // Normal listing with filters
          documentsService.list({
            page: Math.floor(params.page / params.page_size),
            page_size: params.page_size,
            ordering: params.ordering,
            search: params.fullText ? null : params.search,
            content_type: params.content_type,
          })

    promise
      .then((resp) => {
        setDocuments(resp.results)
        setPagesCnt(Math.ceil(resp.count / params.page_size))
        setError(false)
      })
      .catch(() => {
        setError(true)
      })
      .finally(() => setLoading(false))
  }, [params])

  return (
    <Main>
      <PageHeader
        leading={
          <span className="mt-1 inline-flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FolderArchive className="h-5 w-5" />
          </span>
        }
        title={t('documents')}
        description={t('documents_desc')}
      />

      <div className="-mx-4 flex-1 overflow-auto px-4 py-1 lg:flex-row lg:space-x-12 lg:space-y-0">
        {loading ? (
          <DataTableSkeleton columns={6} rows={10} />
        ) : error ? (
          <div className="py-8 text-center text-muted-foreground">
            {t('table_load_error')}
          </div>
        ) : (
          <DataTable
            columns={getColumns(params.fullText)}
            data={documents}
            pages={pagesCnt}
            params={params}
            setParams={setParams}
            ToolbarName={DocumentsToolbar}
            storageKey="documents-table"
          />
        )}
      </div>
    </Main>
  )
}
